import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  changeTenantLicense,
  createAdminTenant,
  listAdminTenants,
  updateTenantStatus,
  type AdminTenantsFilter,
  type ChangeTenantLicenseRequest,
  type CreateTenantAdminRequest,
  type PagedAdminTenantsResponse,
  type TenantAdminSummaryResponse,
  type UpdateTenantStatusRequest,
} from '../api';

export function useAdminTenants(filter: AdminTenantsFilter): UseQueryResult<PagedAdminTenantsResponse> {
  return useQuery({
    queryKey: AdminQueryKeys.tenants(filter),
    queryFn: ({ signal }) => listAdminTenants(filter, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation<TenantAdminSummaryResponse, Error, CreateTenantAdminRequest>({
    mutationFn: (req) => createAdminTenant(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

export function useUpdateTenantStatus() {
  const qc = useQueryClient();
  return useMutation<null, Error, { id: string; request: UpdateTenantStatusRequest }>({
    mutationFn: ({ id, request }) => updateTenantStatus(id, request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

export function useChangeTenantLicense() {
  const qc = useQueryClient();
  return useMutation<null, Error, { id: string; request: ChangeTenantLicenseRequest }>({
    mutationFn: ({ id, request }) => changeTenantLicense(id, request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}
